import { useState, useEffect, useRef } from "react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import slider1 from "../image/videos/slider1.mp4"
import slider2 from "../image/videos/slider2.mp4"
import slider3 from "../image/videos/slider3.mp4"
import slider4 from "../image/videos/slider4.mp4"

const slides = [
  {

    src: slider1,
    title: "Welcome to DYC ONE",
    subtitle: "Inspection · Integrity · Quality",
  },
  { src: slider2 },
  { src: slider3 },
  { src: slider4 }

];

// gradient fallback colours per slide so it still looks good without video
const gradients = [
  "from-[#060d1f] via-[#0b1a3a] to-[#071020]",
  "from-[#07101f] via-[#0d1e38] to-[#060e1c]",
  "from-[#050f1c] via-[#091629] to-[#060c17]",
];

export default function Home() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const videoRefs = useRef([]);
  const timerRef = useRef(null);

  const goTo = (idx) => {
    if (animating || idx === current) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  };

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = () => goTo((current + 1) % slides.length);

  // auto-advance every 6s
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timerRef.current);
  }, []);

  // play the active video, pause others
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === current) {
        v.currentTime = 0;
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [current]);

  const slide = slides[current];

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#060c1a]">

      {/* video / gradient backgrounds */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            i === current ? "opacity-100" : "opacity-0"
          }`}
        >
          {s.src ? (
            <video
              ref={(el) => (videoRefs.current[i] = el)}
              src={s.src}
              preload={i === current ? "auto" : "none"}
              muted
              loop
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            // gradient placeholder until you add the video files
            <div className={`absolute inset-0 bg-gradient-to-br ${gradients[i]}`}>
              {/* subtle grid overlay */}
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
                  backgroundSize: "32px 32px",
                }}
              />
              {/* ambient blobs */}
              <div className="absolute top-[-10%] right-[-5%] w-[40vw] h-[40vw] rounded-full bg-blue-700/20 blur-[120px]" />
              <div className="absolute bottom-[-10%] left-[-5%] w-[35vw] h-[35vw] rounded-full bg-indigo-700/15 blur-[100px]" />
            </div>
          )}
          {/* dark overlay so text is always readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
      ))}

      {/* slide content */}
      <div
        className={`relative z-10 flex flex-col items-center justify-center h-full text-center px-6 transition-all duration-400 ${
          animating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
        }`}
      >
        {/* title */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)] max-w-4xl">
          {slide.title}
        </h1>

        {/* subtitle */}
        <p className="mt-4 text-base sm:text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
          {slide.subtitle}
        </p>
      </div>

      {/* prev / next arrows */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/30 border border-white/15 text-white/80 backdrop-blur-sm hover:bg-black/55 hover:text-white transition-all duration-200"
      >
        <MdKeyboardArrowLeft className="text-xl" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next slide"
        className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-black/30 border border-white/15 text-white/80 backdrop-blur-sm hover:bg-black/55 hover:text-white transition-all duration-200"
      >
        <MdKeyboardArrowRight className="text-xl" />
      </button>

      {/* dot indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`rounded-full transition-all duration-300 ${
              i === current ? "w-7 h-2 bg-white" : "w-2 h-2 bg-white/35 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
}
